import { Input } from 'src/components/widgets/input';
import { AdvancedSelect, SelectionValue } from '../../widgets/select';
import { Dialog, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { PrimaryButton } from '../../widgets/buttons';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import React, { FC, Suspense, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { graphql, useMutation } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import styled from 'styled-components';
import config from '../../../shared/config';
import { ApiLabel, ButtonContainer, DoubleHr, InstructionItem, InstructionLabel, Note, PointedInstructionsList } from '../../../shared/connectDialogStyle';
import { Loader } from '../../../shared/docsDialog';
import { T, toGlobalID } from '../../../shared/helpers';
import { useValidationContext, validateRequired, ValidationStateProvider } from '../../../shared/validation';
import ApiKeys from '../myAccount/apiKeys';
import { Hr } from '../platformSelection/platformConnection';
import { cCloudIntegrationSalesChannelDialogQuery } from './__generated__/cCloudIntegrationSalesChannelDialogQuery.graphql';
import { cCloudIntegrationSalesChannelDialogSaveMutation } from './__generated__/cCloudIntegrationSalesChannelDialogSaveMutation.graphql';
import Stack from '../../widgets/stack/stack';
import { ValidationBox } from '../../widgets/validation/validationBox';

interface CCloudApiProps {
    title?: string;
    salesChannelID?: string | undefined;
    windowDecorator?: FC<any> | undefined;
}

interface Currency {
    label: string;
    value: string;
}

const DescriptionLabel = styled.div`
    display: flex;
    margin-top: 8px;

    > label {
        color: #445a64;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
    }
`;

const cCloudInfo = graphql`
    query cCloudIntegrationSalesChannelDialogQuery {
        ecommerceSettings {
            ecommerceID
            ecommerceName
            apiKey
            apiSecret
            remoteUrl
            currencyGlobalID
            username # This is the siteId
        }
        currencies {
            id
            name
        }
    }
`;

const saveCCloud = graphql`
    mutation cCloudIntegrationSalesChannelDialogSaveMutation($input: EcommerceSettingsInput!) {
        saveEcommerceSettings(settingsInput: $input) {
            ecommerceID
        }
    }
`;

const CCloudIntegrationSalesChannelDialogContent: FC<CCloudApiProps> = (ccloud) => {
    const query = useLazyLoadQuery<cCloudIntegrationSalesChannelDialogQuery>(cCloudInfo, {});
    const [apiKey, setApiKey] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.apiKey ?? '') : '');
    const [apiSecret, setApiSecret] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.apiSecret ?? '') : '');
    const [remoteUrl, setRemoteUrl] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.remoteUrl ?? '') : '');
    const [siteId, setSiteId] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.username ?? '') : '');
    const [currency, setCurrency] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.currencyGlobalID ?? '') : '');
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const validationContext = useValidationContext();

    const currenciesOptions =
        query.currencies?.map((currency) => ({
            value: currency!.id,
            label: currency!.name ?? ''
        })) ?? [];

    const { closeDialog } = useDialogManager();
    const isForEdit = ccloud.salesChannelID !== undefined;

    const [save, isSaving] = useMutation<cCloudIntegrationSalesChannelDialogSaveMutation>(saveCCloud);

    const handleSave = async () => {
        if (!validationContext.submit()) return;

        const loadingNotification = showInfoNotification({
            message: T._('Saving...', 'Admin')
        });

        if (!isSaving) {
            save({
                variables: {
                    input: {
                        ecommerceGlobalID: toGlobalID(11, 'ECommerce'),
                        salesChannelID: toGlobalID(1, 'SalesChannel'),
                        recaptchaToken,
                        apiKey,
                        apiSecret,
                        remoteUrl,
                        siteId,
                        currencyGlobalID: currency
                    }
                },
                onCompleted: () => {
                    removeNotification(loadingNotification);
                    showSuccessNotification({
                        message: T._('Saved', 'Admin')
                    });

                    closeDialog('sales-force');
                },
                onError: () => {
                    removeNotification(loadingNotification);
                    showErrorNotification({
                        message: T._('An error occurred while saving.', 'Admin')
                    });
                }
            });
        }
    };

    const handleSelectedCurrency = (item: SelectionValue<Currency>) => {
        if (item != null && !Array.isArray(item)) {
            setCurrency(item.value);
        }
    };

    return (
        <Dialog windowDecorator={ccloud.windowDecorator} size='large' onClose={() => closeDialog('sales-force')} title={ccloud?.title ?? ''}>
            <Suspense fallback={Loader}>
                <div>
                    {!isForEdit && (
                        <>
                            <InstructionLabel dangerouslySetInnerHTML={{ __html: `<strong>PLEASE NOTE</strong>` }} />
                            <Note
                                dangerouslySetInnerHTML={{
                                    __html: `Zakeke supports integration with both SFRA and SiteGenesis. Depending on your case, please follow the dedicated guide:`
                                }}
                            />
                            <PointedInstructionsList>
                                <InstructionItem
                                    dangerouslySetInnerHTML={{
                                        __html: `<a href='https://zakeke.zendesk.com/hc/en-us/articles/360011323080-SFRA' target='_blank' rel='noopener'>SFRA</a>`
                                    }}
                                />
                                <InstructionItem
                                    dangerouslySetInnerHTML={{
                                        __html: `<a href='https://zakeke.zendesk.com/hc/en-us/articles/360011446679-Sitegenesis' target='_blank' rel='noopener'>SiteGenesis</a>`
                                    }}
                                />
                            </PointedInstructionsList>
                            <Note
                                dangerouslySetInnerHTML={{
                                    __html: `During the installation process, you will be required to fill out the form below to complete the configuration of your Zakeke account.`
                                }}
                            />
                            <Hr />
                        </>
                    )}
                    <ApiLabel dangerouslySetInnerHTML={{ __html: `<strong><h2>Integration settings</h2></strong>` }} />
                    <Stack>
                        <FormControl label={T._('CCloud client ID', 'Admin')}>
                            <Input
                                value={apiKey}
                                placeholder={T._('Insert your store URL', 'Admin')}
                                onChange={(e) => setApiKey(e.currentTarget.value)}
                                validate={validateRequired}
                                validationKey='clientId'
                                validationLabel={T._('CCloud client ID', 'Admin')}
                                validationMessage={T._('CCloud client ID is required.', 'Admin')}
                            />
                        </FormControl>
                        <FormControl label={T._('CCloud secret key', 'Admin')}>
                            <Input
                                value={apiSecret}
                                placeholder={T._('Insert your store URL', 'Admin')}
                                onChange={(e) => setApiSecret(e.currentTarget.value)}
                                validate={validateRequired}
                                validationKey='clientSecret'
                                validationLabel={T._('CCloud secret key', 'Admin')}
                                validationMessage={T._('CCloud secret key is required.', 'Admin')}
                            />
                        </FormControl>
                        <FormControl label={T._('CCloud site key', 'Admin')}>
                            <Input
                                value={siteId}
                                placeholder={T._('Insert your store URL', 'Admin')}
                                onChange={(e) => setSiteId(e.currentTarget.value)}
                                validate={validateRequired}
                                validationKey='siteId'
                                validationLabel={T._('CCloud site key', 'Admin')}
                                validationMessage={T._('CCloud site key is required.', 'Admin')}
                            />
                        </FormControl>
                        <FormControl label={T._('CCloud site URL', 'Admin')}>
                            <Input
                                value={remoteUrl}
                                placeholder={T._('Insert your store URL', 'Admin')}
                                onChange={(e) => setRemoteUrl(e.currentTarget.value)}
                                validate={validateRequired}
                                validationKey='storeURL'
                                validationLabel={T._('CCloud site URL', 'Admin')}
                                validationMessage={T._('CCloud site URL is required.', 'Admin')}
                            />
                        </FormControl>
                        <FormControl label={T._('Currency', 'Admin')}>
                            <AdvancedSelect
                                placeholder={T._('US Dollar', 'Admin')}
                                isMulti={false}
                                options={currenciesOptions}
                                value={currenciesOptions.find((option) => option.value == currency)}
                                onChange={handleSelectedCurrency}
                                validationKey='currency'
                                validationLabel={T._('Currency', 'Admin')}
                                validate={(value) => !value}
                                validationMessage={T._('Currency is required.', 'Admin')}
                            />
                            <DescriptionLabel>
                                <label>
                                    {T._(
                                        `Select your store's base currency carefully to ensure proper integration and accurate transaction fees; incorrect selection may cause issues.`,
                                        'Admin'
                                    )}
                                </label>
                            </DescriptionLabel>
                        </FormControl>
                        <FormControl>
                            <ValidationBox
                                validationKey='cCloudSalesChannelRecaptcha'
                                validationLabel={T._('ReCAPTCHA', 'Admin')}
                                validationValue={recaptchaToken}
                                validate={(value) => !value}
                                validationMessage={T._('ReCAPTCHA is required.', 'Admin')}
                            >
                                <ReCAPTCHA
                                    sitekey={config.googleRecaptchaSitekey}
                                    theme={'light'}
                                    size={'normal'}
                                    onChange={(e) => e && setRecaptchaToken(e)}
                                ></ReCAPTCHA>
                            </ValidationBox>
                        </FormControl>
                    </Stack>
                    <ButtonContainer>
                        <PrimaryButton onClick={handleSave}>{T._('Save', 'Admin')}</PrimaryButton>
                    </ButtonContainer>
                    {isForEdit && (
                        <>
                            <DoubleHr />
                            <ApiLabel dangerouslySetInnerHTML={{ __html: `<strong><h2>API Keys</h2></strong>` }} />
                            <ApiKeys salesChannelID={ccloud.salesChannelID!} />
                        </>
                    )}
                </div>
            </Suspense>
        </Dialog>
    );
};

const CCloudIntegrationSalesChannelDialog: FC<CCloudApiProps> = (props) => (
    <ValidationStateProvider>
        <CCloudIntegrationSalesChannelDialogContent {...props} />
    </ValidationStateProvider>
);

export default CCloudIntegrationSalesChannelDialog;
